/**
 * VisualizationRenderer - Switch component that renders the appropriate visualization
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  VisualizationData,
  BarChartVisualization,
  PieChartVisualization,
  LineChartVisualization,
  AreaChartVisualization,
  TableVisualization,
  SummaryCardVisualization,
  BulletListVisualization,
} from './types';
import { AgentBarChart } from './AgentBarChart';
import { AgentPieChart } from './AgentPieChart';
import { AgentLineChart } from './AgentLineChart';
import { AgentAreaChart } from './AgentAreaChart';
import { AgentTable } from './AgentTable';
import { AgentSummaryCard } from './AgentSummaryCard';
import { AgentBulletList } from './AgentBulletList';

interface VisualizationRendererProps {
  visualization: VisualizationData;
  className?: string;
}

export const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({
  visualization,
  className,
}) => {
  if (!visualization || !visualization.type) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
        <AlertCircle className="h-4 w-4" />
        <span>Invalid visualization data</span>
      </div>
    );
  }

  const wrapperClass = cn(
    "bg-muted/30 rounded-lg p-4 border border-border/50",
    className
  );

  switch (visualization.type) {
    case 'bar_chart':
      return (
        <div className={wrapperClass}>
          <AgentBarChart data={visualization as BarChartVisualization} />
        </div>
      );

    case 'pie_chart':
      return (
        <div className={wrapperClass}>
          <AgentPieChart data={visualization as PieChartVisualization} />
        </div>
      );

    case 'line_chart':
      return (
        <div className={wrapperClass}>
          <AgentLineChart data={visualization as LineChartVisualization} />
        </div>
      );

    case 'area_chart':
      return (
        <div className={wrapperClass}>
          <AgentAreaChart data={visualization as AreaChartVisualization} />
        </div>
      );

    case 'table':
      return (
        <div className={wrapperClass}>
          <AgentTable data={visualization as TableVisualization} />
        </div>
      );

    case 'summary_card':
      return (
        <div className={cn("py-2", className)}>
          <AgentSummaryCard data={visualization as SummaryCardVisualization} />
        </div>
      );

    case 'bullet_list':
      return (
        <div className={cn("py-2", className)}>
          <AgentBulletList data={visualization as BulletListVisualization} />
        </div>
      );

    default:
      return (
        <div className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
          <AlertCircle className="h-4 w-4" />
          <span>Unknown visualization type: {(visualization as any).type}</span>
        </div>
      );
  }
};
